// Shared username availability check used by Register and RegisterFinish.
// Server contract: /api/auth/username-available?u=xxx returns
//   { available: true } or { available: false, reason: 'format' | 'unavailable' }
// Format errors get a specific message; everything else is collapsed by
// the server to "unavailable" to prevent reserved/taken enumeration.

import { api } from './api';

export type AvailabilityState =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'available' }
  | { status: 'unavailable'; reason: 'format' | 'unavailable' };

const MIN_LENGTH = 3;

export async function fetchAvailability(value: string): Promise<AvailabilityState> {
  if (value.length < MIN_LENGTH) return { status: 'idle' };
  try {
    const params = new URLSearchParams({ u: value });
    const res = await api<{ available: boolean; reason?: string }>(
      `/api/auth/username-available?${params.toString()}`,
    );
    if (res.available) return { status: 'available' };
    const reason = res.reason === 'format' ? 'format' : 'unavailable';
    return { status: 'unavailable', reason };
  } catch {
    // Network failure leaves the live check silent — the form submit is
    // the source of truth for whether a name is actually usable.
    return { status: 'idle' };
  }
}

export function hintFor(state: AvailabilityState): string {
  switch (state.status) {
    case 'checking':
      return 'Checking…';
    case 'available':
      return 'Available.';
    case 'unavailable':
      return state.reason === 'format'
        ? '3–32 chars: lowercase, numbers, underscores.'
        : 'Not available.';
    case 'idle':
      return '3–32 characters: lowercase letters, numbers, underscores.';
  }
}

/**
 * Normalize for the server: trim + lowercase. Use on every keystroke so
 * the live-check value matches what gets POSTed at submit time.
 */
export function normalize(value: string): string {
  return value.trim().toLowerCase();
}
